import * as React from 'react';
import { URLX, isInternalUrl, equalsUrl, relativeUrl, currentUrl } from './url';
import { containerElementId } from './constants';
import { pushState, replaceState, type HistoryOptions, type ClientParams } from './client/history';
import { scrollToHash } from './scrolling';

const spaLinkAttribute = 'data-spa-link';
const spaLinksContainerAttribute = 'data-spa-links-container';

export type LinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick'> & {
  to?: URLX | string | { url: string };
  clientParams?: ClientParams;
  linkIdentifier?: string;
  replaceState?: boolean;
  onPageLoaded?: (pageData: any) => void;
  onPageLoadFailed?: (error: any) => void;
  componentName?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => Promise<unknown> | void;
  shouldUseCache?: boolean | ((cachedData: Excite.ContentApi.IComponentData) => boolean);
  keepComponents?: boolean;
};

// Since we both handle clicks in onClick props in React as well as a fallback
// to a global click listener for links that aren't hydrated yet we use
// this as a marker on the native event to say if the event has been handled
// by React.
const exciteClickHandled = 'exciteClickHandled';

let linkUrlDecorator: ((url: URLX, link: HTMLAnchorElement) => URLX) | undefined;

export function setLinkUrlDecorator(decorator: (url: URLX, link: HTMLAnchorElement) => URLX) {
  linkUrlDecorator = decorator;
}

async function onLinkClick(
  link: HTMLAnchorElement,
  e: React.MouseEvent<HTMLAnchorElement> | MouseEvent,
  linkProps?: LinkProps,
  deferLoadPromise?: Promise<any>,
) {
  const currUrl = currentUrl();
  if (!/^http[s]?:/.test(link.href) || !isNormalClick(e) || isExternalLink(link, currUrl)) {
    return Promise.resolve();
  }

  const reactEvent = e as React.MouseEvent<HTMLAnchorElement>;
  if (reactEvent.nativeEvent) {
    (reactEvent.nativeEvent as any)[exciteClickHandled] = true;
  }

  e.preventDefault();

  const options: HistoryOptions = {
    linkIdentifier: linkProps?.linkIdentifier,
    shouldUseCache: linkProps?.shouldUseCache,
    componentName: linkProps?.componentName,
    clientParams: linkProps?.clientParams,
    keepComponents: linkProps?.keepComponents,
    deferLoadPromise,
  };
  const url = new URLX(link.href);
  const linkUrl = linkUrlDecorator ? linkUrlDecorator(url, link) : url;
  const promise =
    equalsUrl(currUrl, linkUrl, true) || (linkProps && linkProps.replaceState)
      ? await replaceState(linkUrl, options)
      : await pushState(linkUrl, options);

  if (link.hash) {
    scrollToHash(link.hash);
  }

  return promise;
}

function onClickProp(props: LinkProps, e: React.MouseEvent<HTMLAnchorElement>) {
  let deferLoadPromise: Promise<unknown> | undefined = undefined;
  if (props.onClick) {
    deferLoadPromise = props.onClick(e) as Promise<any>;
    if (typeof deferLoadPromise !== 'object' || !deferLoadPromise.then) {
      deferLoadPromise = undefined;
    }
  }
  if (!e.isDefaultPrevented()) {
    onLinkClick(e.currentTarget, e, props, deferLoadPromise).then(
      result => {
        if (props.onPageLoaded) {
          props.onPageLoaded(result);
        }
      },
      result => {
        if (props.onPageLoadFailed) {
          props.onPageLoadFailed(result);
        }
      },
    );
  }
}

export const Link = React.forwardRef(function Link(props: LinkProps, ref: React.Ref<HTMLAnchorElement>) {
  const {
    to,
    linkIdentifier,
    replaceState,
    onPageLoaded,
    onPageLoadFailed,
    shouldUseCache,
    componentName,
    clientParams,
    keepComponents = false,
    ...restProps
  } = props;
  props = { ...props, keepComponents };

  const currUrl = currentUrl();

  let url = React.useMemo((): URLX | undefined => {
    if (props.to) {
      if (typeof props.to === 'string') {
        return new URLX(props.to);
      } else if ('url' in props.to) {
        return new URLX(props.to.url);
      } else {
        return props.to;
      }
    }
  }, [props.to]);

  if (clientParams) {
    url = url ? new URLX(url) : currUrl;
    for (const key of Object.keys(clientParams)) {
      url.searchParams.set(key, clientParams[key]?.toString() ?? '');
    }
  }

  if (url && !isInternalUrl(url, currUrl)) {
    return (
      <a {...restProps} href={url.href} ref={ref}>
        {props.children}
      </a>
    );
  } else {
    (restProps as any)[spaLinkAttribute] = 'true';

    const href = url && relativeUrl(url, currUrl);

    // are not considerered links by user agents without href (accessability)
    return (
      <a {...restProps} href={href || ''} onClick={e => onClickProp(props, e)} ref={ref}>
        {props.children}
      </a>
    );
  }
});

export function LinksContainer(props: { children: React.ReactNode; component?: string }) {
  const onClick = (e: React.MouseEvent) => {
    const link = clickedLink(e.nativeEvent);
    if (link && !e.isDefaultPrevented()) {
      onLinkClick(link, e as React.MouseEvent<HTMLAnchorElement>);
    }
  };
  return (
    <div {...{ [spaLinksContainerAttribute]: 'true' }} onClick={onClick}>
      {props.children}
    </div>
  );
}

export function isExternalLink(link: HTMLAnchorElement, currUrl: URLX) {
  return link.target === '_blank' || !isInternalUrl(new URLX(link.href), currUrl);
}

export function isHashLink(link: HTMLAnchorElement) {
  // In IE `link.hostname` equals an empty string if the location is relative
  return (
    (link.hostname === '' || link.hostname === window.location.hostname) &&
    link.pathname === window.location.pathname &&
    link.search === window.location.search &&
    link.hash !== ''
  );
}

function clickedLink(e: Event) {
  let target: HTMLElement | null = e.target as HTMLElement;
  do {
    if (target.tagName === 'A') {
      return target as HTMLAnchorElement;
    }
    // tslint:disable-next-line no-conditional-assignment
  } while ((target = target.parentElement));
  return undefined;
}

function isNormalClick(e: React.MouseEvent<HTMLAnchorElement> | MouseEvent) {
  return !(e.button > 1 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey);
}

if (__DEV__) {
  if (__BROWSER__) {
    const parentHasAttributeOrId = (link: HTMLAnchorElement, attributeNameOrId: string) => {
      let current: HTMLElement | null = link;
      while (current) {
        if (current.hasAttribute(attributeNameOrId) || current.id === attributeNameOrId) {
          return true;
        }
        current = current.parentElement;
      }
      return false;
    };

    document.addEventListener(
      'click',
      e => {
        const link = clickedLink(e);
        if (
          link &&
          isNormalClick(e) &&
          !isExternalLink(link, new URLX(window.location.href)) &&
          !isHashLink(link) &&
          !link.hasAttribute(spaLinkAttribute) &&
          !parentHasAttributeOrId(link, spaLinksContainerAttribute) &&
          parentHasAttributeOrId(link, containerElementId)
        ) {
          e.preventDefault();
          throw new Error(
            'You clicked on link that was not rendered by either <Link> or <LinksContainer>. You should never render a <a> directly. ' +
            "Render the links with @avensia/excite/Link or @avensia/excite/LinksContainer if you're forced to render third party HTML.",
          );
        }
      },
      true,
    );
  }
}

if (__BROWSER__) {
  (async () => {
    // It's important to wait for the initial render to be done first
    // because we want our click handler to be registered after React
    const { initialRenderDone } = await import('./client/bootstrap');
    await initialRenderDone;
    document.addEventListener('click', e => {
      const link = clickedLink(e);
      if (
        link &&
        isNormalClick(e) &&
        !isExternalLink(link, new URLX(window.location.href)) &&
        !isHashLink(link) &&
        link.hasAttribute(spaLinkAttribute) &&
        !(e as any)[exciteClickHandled] &&
        !e.defaultPrevented
      ) {
        // We do this because it's possible that we've server rendered a link
        // but we have choosen not to hydrate all components yet. But we still
        // want rendered links to be clickable without causing a full page load.
        e.preventDefault();
        onLinkClick(link, e);
        }
    });
  })();
}
